import { useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";

import { ChartLegend } from "@/components/Charts/ChartLegend";
import { dataColors } from "@/components/Charts/theme";
import { parseChartDate } from "@/components/Charts/util";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { NoData } from "@/components/NoData/NoData";
import { Stack } from "@/design-system/Stack";
import { LineChart } from "@/modules/analytics/components/LineChart";
import type { DateRangeOption } from "@/modules/analytics/util";
import { filterDataByDateRange } from "@/modules/analytics/util";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { useTranslation } from "@/translations";
import type { NonEmptyArray } from "@/types/utility-types";
import { RuntimeColorDotLabel } from "@/ui-adapters/RuntimeColorBadge";

import { AnalyticsCard } from "../../../components/AnalyticsCard";

interface Props {
  dateRangeOptions: NonEmptyArray<DateRangeOption>;
}

const RANGE = "lastYear";

export function SocialEnvironmentalResidentActivityCard({ dateRangeOptions }: Props): React.ReactNode {
  const { t, i18n } = useTranslation();
  const analyticsQueries = useAnalyticsQueries();
  const [dateRange, setDateRange] = useState<DateRangeOption>(dateRangeOptions[0]);
  const { data } = useSuspenseQuery(analyticsQueries.messageLabelStats(RANGE));

  const trimmedData = filterDataByDateRange(data.data, dateRange);

  const mappedAskHelpData =
    trimmedData.map((x) => ({
      x: parseChartDate(x.range).toISOString(),
      y: x.askHelpCount,
      isPending: x.isCurrentMonth,
    })) || [];

  const mappedInformData =
    trimmedData.map((x) => ({
      x: parseChartDate(x.range).toISOString(),
      y: x.informCount,
      isPending: x.isCurrentMonth,
    })) || [];

  const mappedSellItemData =
    trimmedData.map((x) => ({
      x: parseChartDate(x.range).toISOString(),
      y: x.sellItemCount,
      isPending: x.isCurrentMonth,
    })) || [];

  const noData = !data || data.data.every((x) => x.askHelpCount === 0 && x.informCount === 0 && x.sellItemCount === 0);

  return (
    <AnalyticsCard.Root variant="graph">
      <AnalyticsCard.Header
        title={t("page.analytics-overview.chart.message-types.title")}
        tooltip={t("page.analytics-overview.chart.message-types.title-tooltip")}
      >
        <AnalyticsCard.FilterSelect
          items={dateRangeOptions}
          selected={dateRange}
          onChange={setDateRange}
          renderOption={(x) => x.name}
          keySelector={(x) => x.value}
        />
      </AnalyticsCard.Header>
      {noData ? (
        <NoData />
      ) : (
        <>
          <LineChart
            data={[
              {
                dataPoints: mappedAskHelpData,
                color: dataColors.primary,
                label: t("page.analytics-overview.chart.message-types.hover.help"),
              },
              {
                dataPoints: mappedInformData,
                color: dataColors.mint,
                label: t("page.analytics-overview.chart.message-types.hover.inform"),
              },
              {
                dataPoints: mappedSellItemData,
                color: dataColors.orange,
                label: t("page.analytics-overview.chart.message-types.hover.sell"),
              },
            ]}
            maxY={5}
            hoverLabel={({ x, relatedPoints }) => {
              const isPending = relatedPoints.some((point) => point.dataPoint.isPending);

              return (
                <Stack>
                  <span>{formatDate(i18n, "monthYear", x)}</span>
                  {relatedPoints.map((point) => (
                    <RuntimeColorDotLabel color={point.color} key={point.label}>
                      <span className="flex flex-1 justify-between gap-space-sm">
                        <span>{point.label}:</span>
                        <span>{point.dataPoint.y}</span>
                      </span>
                    </RuntimeColorDotLabel>
                  ))}
                  {isPending && (
                    <span>{t("page.analytics-details.engagement.chart.tooltip.data-collection-in-progress")}</span>
                  )}
                </Stack>
              );
            }}
            xAxisType="date"
            yLabel={t("page.analytics-overview.chart.message-types.y-axis-label")}
          />
          <ChartLegend
            items={[
              {
                label: t("page.analytics-overview.chart.message-types.legend.help"),
                color: dataColors.primary,
                tooltip: t("page.analytics-overview.chart.message-types.legend.help.tooltip"),
              },
              {
                label: t("page.analytics-overview.chart.message-types.legend.inform"),
                color: dataColors.mint,
                tooltip: t("page.analytics-overview.chart.message-types.legend.inform.tooltip"),
              },
              {
                label: t("page.analytics-overview.chart.message-types.legend.sell"),
                color: dataColors.orange,
                tooltip: t("page.analytics-overview.chart.message-types.legend.sell.tooltip"),
              },
            ]}
          />
        </>
      )}
    </AnalyticsCard.Root>
  );
}
